/**
 * Hungarian catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Hungarian at a mathematician's register. **Warrant → «Indoklás»**
 * (justification — flagged). Hungarian keeps the noun SINGULAR after a numeral («2 megszorítás»),
 * so the one/other forms coincide — both correct. W/M grades stay Latin
 * (`::numbering-system/latn`). CLDR cardinal categories are one / other, so `frameActive`
 * declares exactly those (plus an explicit `=0`). Per INV-STABLE-ID a wrong translation is a
 * presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const HU_CATALOG = {
    locale: 'hu',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Vizsgálat',
        [P2.sectionClaims]: 'Állítások',
        [P2.sectionIntegrity]: 'Integritás',
        [P2.axisWarrantLabel]: 'Indoklás',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirikus',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definíció} interp {Értelmezés} testable {Tesztelhető} tested {Tesztelt} refuted {Cáfolt} program {Kutatási program} philos {Filozófiai} other {Ismeretlen}}',
        [P2.axisModalLabel]: 'Modális',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Erősségi szint {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Felfedezhető',
        [P2.stateRefuted]: 'Cáfolt',
        [P2.stateOutside]: 'Hatókörön kívül',
        [P2.verdictConflict]: 'Ütközik ezzel: {constraints}',
        [P2.verdictOutside]: 'Igényli: {missing}',
        [P2.lossRegisterPlain]: 'Köznyelvre egyszerűsítve — a pontosság egy része kimarad.',
        [P2.lossRegisterKid]: 'Fiatalabb olvasóknak egyszerűsítve — sok részlet kimarad.',
        [P2.lossProfileDetail]: 'A teljes Indoklás / Empirikus / Modális profil itt összegezve.',
        [P2.lossConflictDetail]: 'Az ütközés részletei ebben a nézetben rövidítve vannak.',
        [P2.laneVerified]: 'Ellenőrzött',
        [P2.laneUnverified]: 'Nem ellenőrzött',
        [P2.laneConflict]: 'Ütközés bizonyítva',
        [P2.constraintLogic]: 'Logika',
        [P2.constraintLightBends]: 'A fény elhajlik a gravitációs mezőben',
        [P2.constraintParallelPostulate]: 'A párhuzamossági posztulátum',
        [P2.claimScalarGravity]: 'Skaláris gravitációelmélet',
        [P2.claimNonEuclidean]: 'Nemeuklideszi geometria',
        [P2.claimSubstrateSr]: 'A speciális relativitáselmélet szubsztrátum-értelmezése',
        [P2.edgeExtObservation]: 'Külső megfigyelés',
        [P2.edgeResourceSample]: 'Megosztott számítási erőforrás',
        [P2.edgeHeuristicSample]: 'Heurisztikus útmutatás',
        [P2.edgeSharedSample]: 'Közös alap',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Megszorítások',
        [P2.sectionEdges]: 'Élek',
        [P2.sectionFrame]: 'Keret',
        [P2.sectionExposure]: 'Kitettség',
        [P2.statusAdoptedFormal]: 'Elfogadva (formális)',
        [P2.statusAdoptedInterpretive]: 'Elfogadva (értelmező)',
        [P2.statusSuspended]: 'Felfüggesztve',
        [P2.edgeEffectConstraint]: 'Megszorítást alkot (cáfolhat)',
        [P2.edgeEffectResource]: 'Erőforrás — nincs hatása az ítéletekre',
        [P2.edgeEffectHeuristic]: 'Heurisztika — nincs hatása az ítéletekre',
        [P2.edgeEffectShared]: 'Közös alap — nincs hatása az ítéletekre',
        [P2.frameActive]: '{count, plural, =0 {Nincs aktív megszorítás} one {# aktív megszorítás} other {# aktív megszorítás}}',
        [P2.exposureNone]: 'Nincsenek értelmező függőségek',
        [P2.exposureDep]: 'Függ ettől: {id}',
        [P2.laneTrackMachine]: 'Gép',
        [P2.laneTrackExpert]: 'Szakértő',
        [P2.laneTrackCommunity]: 'Közösség',
        [P2.expertAudited]: 'Auditált',
        [P2.expertInProgress]: 'Folyamatban',
        [P2.expertNotReviewed]: 'Nem vizsgált',
        [P2.communityFlags]: 'Vannak jelölések',
        [P2.communityNone]: 'Nincsenek jelölések',
        [P2.controlChallenge]: 'Kifogásolás',
        [P2.controlActivate]: 'Aktiválás',
        [P2.controlMakeInterpretive]: 'Értelmezővé tétel',
        [P2.controlImportEdge]: 'Él importálása',
        [P2.controlPromote]: 'Előléptetés',
        [P2.controlUndo]: 'Visszavonás',
        [P2.controlRedo]: 'Újra',
        [P2.controlRegister]: 'Regisztrálás',
        [P2.promoteRejected]: 'Előléptetés elutasítva',
        [P2.promoteAccepted]: 'Előléptetés elfogadva',
        [P2.promoteReasonRefutationTerminal]: 'Egy bizonyítás nem orvosolhat egy cáfolatot.',
        [P2.promoteReasonKeystone]: 'A záróköves előléptetéshez empirikus alátámasztás kell.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Az empirikus típus nincs rögzítve.',
        [P2.promoteReasonModalNotFixed]: 'A modális fokozat nincs rögzítve.',
        [P2.promoteReasonWarrantNotImproved]: 'Az indoklási fokozat nem javult.',
        [P2.evidenceComputation]: "{engine} kiszámította: {op} = {result} ({config} alatt)",
        [P2.lossNumericRounding]: "Az eredmény {dp} tizedesjegyre kerekítve",
    },
};
//# sourceMappingURL=hu.js.map